/**
 * Desktop Auth0 React context + hooks.
 *
 * Kept out of `provider.tsx` so that file only exports `DesktopAuthProvider`
 * (react-refresh/only-export-components ratchet).
 */
import { createContext, useContext } from 'react';

import { logger } from '@/utils/logger/logger';

import type { DesktopAuthLoginOptions, DesktopAuthSession } from './index';

export type DesktopAuthContextValue = {
  session: DesktopAuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Starts a desktop sign-in. Resolves `true` when a login attempt was
   * actually launched (browser opened, pending attempt marked), `false` when
   * the call was a no-op (outside DesktopAuthProvider, or the Tauri bridge is
   * unavailable) — a positive signal, so callers never have to infer
   * "nothing launched" from the absence of pending state (MUL-3346 review).
   */
  login: (options?: DesktopAuthLoginOptions) => Promise<boolean>;
  logout: () => Promise<void>;
};

export const DesktopAuthContext = createContext<DesktopAuthContextValue | undefined>(
  undefined
);

export function useDesktopAuth(): DesktopAuthContextValue {
  const context = useContext(DesktopAuthContext);
  if (context === undefined) {
    throw new Error('useDesktopAuth must be used within a DesktopAuthProvider');
  }

  return context;
}

/**
 * Default value for useDesktopAuth when outside of DesktopAuthProvider.
 * Used by hooks that need to work in both web and desktop contexts.
 */
const defaultDesktopAuthValue: DesktopAuthContextValue = {
  session: null,
  isAuthenticated: false,
  isLoading: false,
  login: () => {
    logger.warn(
      '[useDesktopAuthSafe] login() called outside DesktopAuthProvider - this is a no-op on web'
    );
    return Promise.resolve(false);
  },
  logout: () => {
    logger.warn(
      '[useDesktopAuthSafe] logout() called outside DesktopAuthProvider - this is a no-op on web'
    );
    return Promise.resolve();
  },
};

/**
 * Safe version of useDesktopAuth that returns default values when not inside
 * a DesktopAuthProvider. Use this in hooks/components that need to work in
 * both web and desktop contexts.
 */
export function useDesktopAuthSafe(): DesktopAuthContextValue {
  const context = useContext(DesktopAuthContext);
  return context ?? defaultDesktopAuthValue;
}
